import { ChevronDownIcon } from '@heroicons/react/16/solid';
import React, { useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { Button } from '../catalyst-ui-kit/button';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';
import { InlineSelect } from '../InlineSelect/InlineSelect';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import type { BaseOption, SingleSelectProps } from '../MultiSelect/types';
import { isGroupedOptions } from '../MultiSelect/useMultiSelectState';

type SelectWithFavoritesProps<
  T extends string | number,
  B extends BaseOption<T> = BaseOption<T>,
> = PrettyMerge<
  Omit<SingleSelectProps<T, B>, 'multiselect'>,
  {
    // Starred values to show in inline buttons
    starredValues: T[];
    // Handler for toggling star status of an option
    onToggleStar?: (value: T) => void;
    // Custom component for controlling how a starred option's shortcut is
    // displayed. Defaults to showing option's icon.
    shortcutComponent?: React.ComponentType<{ option: B }>;
    shortcutButtonClassName?: string;
    triggerButtonClassName?: string;
  }
>;

/**
 * Custom select/dropdown component that has a button to star/un-star each
 * option. Starred options are displayed as an inline select, with the remaining
 * option displayed as a dropdown with a chevron trigger.
 */
const OptionWithStar = <T extends string | number>({
  option,
  starredValues,
  onToggleStar,
  children,
  className,
  ...divProps
}: React.ComponentPropsWithRef<'div'> & {
  option: BaseOption<T>;
  starredValues: T[];
  onToggleStar?: (value: T) => void;
} & React.RefAttributes<HTMLElement>) => {
  const isStarred = starredValues.includes(option.value);

  return (
    <div className={cn(className, 'px-2')} {...divProps}>
      {children}
      {/* Display a star button */}
      {onToggleStar && (
        <FavoriteButton
          iconClassName={cn('size-3.5', !isStarred ? 'invisible group-hover:visible' : '')}
          isFavorite={isStarred}
          onToggle={() => onToggleStar(option.value)}
          title={isStarred ? 'Unfavorite (F key)' : 'Favorite (F key)'}
        />
      )}
    </div>
  );
};

// Custom multiselect trigger that displays just a chevron, unless there's no
// starred options in which case it shows the current option.
const ValueOrChevronTrigger = <T extends string | number, B extends BaseOption<T> = BaseOption<T>>({
  shouldShowShortcuts,
  currentOption,
  className,
  getShortcut,
  isOpen = false,
  ...props
}: {
  shouldShowShortcuts: boolean;
  currentOption?: B;
  disabled?: boolean;
  isOpen?: boolean;
  className?: string;
  getShortcut: (option: B) => React.ReactNode;
}) => {
  // If we should show shortcuts or no current value, show chevron
  if (shouldShowShortcuts || !currentOption) {
    return (
      <Button text color="secondary" size="sm" className={cn('group !p-1', className)} {...props}>
        <div className="size-4">
          <ChevronDownIcon
            className={twMerge(
              'h-auto w-full shrink-0 fill-contentSecondary transition-all group-hover:fill-contentPrimary',
              isOpen ? 'rotate-180 fill-contentPrimary' : ''
            )}
            aria-hidden="true"
          />
        </div>
      </Button>
    );
  }

  // Otherwise show the shortcut label for current value
  return (
    <Button
      text
      color="secondary"
      size="sm"
      className={cn(
        'group !p-1',
        {
          'text-contentPrimary': isOpen,
        },
        className
      )}
      {...props}
    >
      {getShortcut(currentOption)}
    </Button>
  );
};

export function SelectWithFavorites<
  T extends string | number,
  B extends BaseOption<T> = BaseOption<T>,
>({
  options,
  value,
  onChange,
  starredValues,
  onToggleStar,
  shortcutComponent: ShortcutComponent,
  className,
  shortcutButtonClassName,
  triggerButtonClassName,
  ...props
}: SelectWithFavoritesProps<T, B>) {
  // Get all available options as flat array
  const allOptions = useMemo(
    () => (isGroupedOptions<T, B>(options) ? options.flatMap((group) => group.options) : options),
    [options]
  );

  // Helper function to get the shortcut for an option
  const getShortcut = useCallback(
    (option: B) => {
      if (ShortcutComponent) {
        return <ShortcutComponent option={option} />;
      }
      // Fallback
      return option.icon ?? option.display ?? option.label ?? String(option.value);
    },
    [ShortcutComponent]
  );

  // Determine inline options to show
  const inlineOptions = useMemo(() => {
    const inlineOptionValues = [
      ...starredValues,
      // Currently selected option, if it's not already starred
      ...(value !== undefined && !starredValues.includes(value) ? [value] : []),
    ];

    // Don't show inline options if the only option is the current value, since
    // it's shown as the multiselect trigger instead.
    if (
      inlineOptionValues.length === 0 ||
      (inlineOptionValues.length === 1 && inlineOptionValues[0] === value)
    ) {
      return [];
    }

    return inlineOptionValues
      .map((inlineOption) => allOptions.find((opt) => opt.value === inlineOption))
      .filter((option) => option !== undefined)
      .map((option) => ({
        value: option.value,
        label: option.label,
        display: getShortcut(option),
        disabled: option.disabled,
      }));
  }, [starredValues, value, allOptions, getShortcut]);

  // Get current value option for trigger
  const currentOption = useMemo(
    () => (value !== undefined ? allOptions.find((opt) => opt.value === value) : undefined),
    [value, allOptions]
  );

  const OptionWithStarWrapper = useMemo(
    () => (props: React.HTMLProps<HTMLDivElement> & { option: BaseOption<T> }) => (
      <OptionWithStar {...props} starredValues={starredValues} onToggleStar={onToggleStar} />
    ),
    [starredValues, onToggleStar]
  );

  const TriggerWrapper = useMemo(
    () => (props: { disabled?: boolean; isOpen?: boolean } & React.RefAttributes<HTMLElement>) => (
      <ValueOrChevronTrigger
        {...props}
        shouldShowShortcuts={inlineOptions.length > 0}
        currentOption={currentOption}
        getShortcut={getShortcut}
        className={triggerButtonClassName}
      />
    ),
    [inlineOptions.length, currentOption, getShortcut, triggerButtonClassName]
  );

  // Handle keyboard shortcuts
  const handleKeyboardShortcut = useCallback(
    (key: string, focusedOption: BaseOption<T> | null) => {
      if (key === 'f' && focusedOption && onToggleStar) {
        onToggleStar(focusedOption.value);
      }
    },
    [onToggleStar]
  );

  return (
    <div className={cn('flex items-center', className)}>
      {inlineOptions.length > 0 && (
        <InlineSelect
          options={inlineOptions}
          selected={value}
          onChange={(option) => option && onChange(option.value)}
          className="gap-0"
          buttonClassName={cn('!p-1', shortcutButtonClassName)}
          activeClassName="text-contentTertiary"
        />
      )}
      <MultiSelect
        options={options}
        value={value}
        onChange={onChange}
        multiselect={false}
        showSearchInput={false}
        clearEnabled={false}
        maxHeight={400}
        optionComponent={OptionWithStarWrapper}
        triggerComponent={TriggerWrapper}
        onKeyboardShortcut={handleKeyboardShortcut}
        className="flex items-center"
        {...props}
      />
    </div>
  );
}
